import { Replay } from "@mui/icons-material"
import {
    Collapse,
    Fade,
    FormControlLabel,
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Switch,
    TextField,
} from "@mui/material"
import { useEffect, useState } from "react"
import { Body, Label } from "../../components/Typography/Typography"
import { useDebouncer } from "../../hooks/useDebouncer"
import { EventRegexExpression, regexTemplateOptions } from "../../models/FlightlineEvent"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { useStyles } from "./styles"

export const FlightlineMatchingExpression = () => {
    const { classes } = useStyles()
    const { active, isCustom, selectedTemplate } = useFlightlineEventStore((state) => state.curRegexExpression)
    const updateMatchingExpression = useFlightlineEventStore((state) => state.updateMatchingExpression)

    // track display value of matchingExpression.custom while debouncing
    const [regExp, setRegExp] = useState(active || "")

    /**
     * Reset regExp if it does not equal new active values
     *
     * Happens when handleCustomChange is run with throttle === true,
     * then ran again with throttle === false before the debouncer
     * is done waiting.
     */
    useEffect(() => {
        if (isCustom && regExp !== active) {
            setRegExp(active || "")
        }
    }, [active])

    /**
     * Updates matching expression.
     *
     * Use debounced if calling multiple times in quick succession.
     *
     * @param {object} data matchingExpression data to update
     */
    const handleMatchingExpression = (data: Partial<EventRegexExpression>) => {
        updateMatchingExpression(data)
    }
    const debounced = useDebouncer(handleMatchingExpression, 160)

    const handleSelectedTemplateChange = (label: string) => {
        const newTemplate = regexTemplateOptions.find((t) => t.label === label)!

        setRegExp(newTemplate.value)
        handleMatchingExpression({
            custom: newTemplate.value,
            selectedTemplate: newTemplate,
        })
    }

    const handleIsCustomChange = () => {
        handleMatchingExpression({ isCustom: !isCustom })
    }

    const handleCustomChange = (label: string, throttle = true) => {
        setRegExp(label)

        if (throttle) {
            debounced({ custom: label })
        } else {
            handleMatchingExpression({ custom: label })
        }
    }

    const handleCustomReset = () => {
        handleCustomChange(selectedTemplate.value, false)
    }

    return (
        <>
            <Label>Flightline Matching Expression</Label>
            <Grid container className={classes.gutterBottom__sm}>
                <Grid size={{ md: 4 }}>
                    <Select
                        variant={"outlined"}
                        fullWidth
                        value={selectedTemplate.label ?? ""}
                        onChange={(e) => handleSelectedTemplateChange(e.target.value)}
                        renderValue={(val) => val}
                    >
                        {regexTemplateOptions.map((temp) => (
                            <MenuItem key={temp.value} value={temp.label}>
                                <Grid className={classes.status_description}>
                                    <Body disableGutter>
                                        {temp.label} {temp.description}
                                    </Body>
                                </Grid>
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid sx={{ display: "flex", alignContent: "center", ml: 2 }}>
                    <FormControlLabel
                        label="Use Advanced Expression"
                        control={<Switch checked={isCustom} color="primary" onChange={handleIsCustomChange} />}
                    />
                </Grid>
            </Grid>

            <Collapse in={isCustom} collapsedSize={20}>
                <Fade in={isCustom}>
                    <Grid className={classes.gutterBottom__lg} size={{ md: 4 }}>
                        <TextField
                            label="Advanced Expression"
                            value={regExp}
                            onChange={(e) => handleCustomChange(e.target.value)}
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2 }}
                            className={classes.input__md}
                            slotProps={{
                                input: {
                                    endAdornment: regExp !== selectedTemplate.value && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={handleCustomReset}>
                                                <Replay />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>
                </Fade>
            </Collapse>
        </>
    )
}
